/**
 * OWA-930 (g2 review, Pi 5): deleting the tasks of a record the user is abandoning.
 *
 * Cancelling a new Case, lead, contact or campaign, or discarding a new task, deletes tasks that
 * were created in this tab during that edit. The generated `deleteTask` waits for the tab's first
 * fetch, because a delete the user asks for must see every comment and document of the task. For
 * these tasks the wait only left them behind on a record that was never kept: anything attached
 * to a task created in this tab reached the tab through its own saves or Socket.IO, not through
 * the first fetch. So these deletes skip the wait. Blockers and finalized invoices still refuse.
 */
import {uuid} from '@/@types/schema';
import Task from '@/entities/task';
import {deleteTask} from '@/store/deps/taskDeps';
import type {DeleteResult, DeleteTraversal} from '@/store/deps/deleteContext';
import {reportDeleteResult} from '@/lib/cascadeDelete';
import {showTaskRefusal, taskDeleteRefusal} from './taskDeletion';

/** The generated result can only say blocked. A refusal here also says why: a finalized invoice cannot be changed. */
export type AbandonedDeleteResult = DeleteResult & {refusal?: string};

/** One delete operation for the tasks of an abandoned record; `rootId` is that record (or task). */
export function abandonedRecordTraversal(rootId: uuid): DeleteTraversal {
    return {visited: new Set(), importSource: 'delete', importId: rootId};
}

/**
 * Delete a task created in this tab for an abandoned record, with its comments and documents.
 *
 * @param task - the abandoned task
 * @param traversal - from `abandonedRecordTraversal`, shared by every task of the operation
 */
export function deleteAbandonedTask(task: Task, traversal: DeleteTraversal): AbandonedDeleteResult {
    const refusal = taskDeleteRefusal(task);
    if (refusal) return {status: 'blocked', impacts: refusal.impacts, refusal: refusal.message};
    return deleteTask(task, undefined, traversal);
}

/** Report a delete that did not happen, with the check's own reason. Returns true when the delete was queued. */
export function reportAbandonedDelete(result: AbandonedDeleteResult): boolean {
    if (result.refusal === undefined) return reportDeleteResult('task', result);
    showTaskRefusal(result.refusal);
    return false;
}
